/**
 * @module taxonomy
 * @description Global taxonomy for categorizing content across entities
 */

import type { ModuleManifest, ModuleContext } from '@gzl10/nexus-sdk'
import { tagsEntity } from './tags.entity.js'
import { tagAssignmentsEntity } from './assignments.entity.js'
import { createTaxonomyService, type TaxonomyService } from './taxonomy.service.js'
import { createTaxonomyRoutes } from './taxonomy.routes.js'

export { tagsEntity }
export { tagAssignmentsEntity }
export { createTaxonomyService, type TaxonomyService }
export { createTaxonomyRoutes }
export type { TagRecord, TagAssignment, TaggedEntity } from './taxonomy.types.js'

interface EntityChangePayload {
  module: string
  entity: string
  action: string
  id: string
}

export const taxonomyModule: ModuleManifest = {
  name: 'taxonomy',
  label: { en: 'Taxonomy', es: 'Taxonomía' },
  icon: 'mdi:tag-multiple',
  description: { en: 'Global taxonomy for categorizing content', es: 'Taxonomía global para categorizar contenido' },
  type: 'core',
  category: 'content',
  definitions: [tagsEntity, tagAssignmentsEntity],

  routePrefix: '/taxonomy',

  pages: [
    {
      id: 'taxonomy-overview',
      label: { en: 'Overview', es: 'Resumen' },
      icon: 'mdi:chart-box-outline',
      type: 'dashboard',
      order: 0,
      widgets: [
        { id: 'stats', type: 'stat', endpoint: '/taxonomy/stats', layout: { span: 6 } },
        { id: 'by-type', type: 'chart', label: { en: 'By entity type', es: 'Por tipo de entidad' }, endpoint: '/taxonomy/stats/by-type', chart: 'donut', layout: { span: 6 } },
        { id: 'top-tags', type: 'table', label: { en: 'Most used tags', es: 'Tags más usados' }, endpoint: '/taxonomy/stats/top-tags', layout: { span: 12 } }
      ]
    }
  ],

  routes: (ctx: ModuleContext) => createTaxonomyRoutes(ctx),

  init: (ctx: ModuleContext) => {
    const taxonomyService = createTaxonomyService(ctx)
    ctx.services.register('taxonomy', taxonomyService)

    // Auto-cleanup: remove tag assignments when any entity is deleted
    ctx.events.on('entity.deleted', async (...args: unknown[]) => {
      const payload = args[0] as EntityChangePayload
      const entityType = `${payload.module}.${payload.entity}`
      try {
        await taxonomyService.removeAllForEntity(entityType, payload.id)
      } catch (err) {
        ctx.core.logger.warn(`Taxonomy: failed to cleanup assignments for ${entityType}:${payload.id}: ${err}`)
      }
    })

    ctx.core.logger.debug('Taxonomy module initialized')
  }
}
